import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import SortableTable from "../../Componentes/Utils/SortableTable";
import { useModal } from "../../contexts/ModalContext";
import { useToast } from "../../contexts/ToastContext";
import useApi from "../../hooks/useApi";
import ModalGerenciarGrupos, {
    MODAL_ID as MODAL_GERENCIAR_GRUPOS_ID,
} from "./ModalGerenciarGrupos";
import ModalNovoGrupo, { MODAL_ID as MODAL_NOVO_GRUPO_ID } from "./ModalNovoGrupo";

const PAGE_SIZE = 100;
const SCROLL_THRESHOLD = 40;
const LIST_USUARIOS_URL = "/Painel/API/AccessManagement/GestaoAcessos/ListUsuarios/";
const LIST_GRUPOS_URL = "/Painel/API/AccessManagement/GestaoAcessos/ListGrupos/";
const LIST_MODULOS_URL = "/Painel/API/AccessManagement/GestaoAcessos/ListModulos/";
const SET_GRUPO_URL = "/Painel/API/AccessManagement/GestaoAcessos/SetGrupo/";

const PageContent = styled.div`
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    gap: 12px;
    box-sizing: border-box;
    overflow: hidden;
`;

const HeaderBar = styled.div`
    width: 100%;
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
    align-items: center;
    flex-shrink: 0;
`;

const Badge = styled.div`
    background: rgba(255, 255, 255, 0.08);
    border: 1px solid rgba(255, 255, 255, 0.12);
    color: rgba(255, 255, 255, 0.9);
    padding: 8px 10px;
    border-radius: 10px;
    font-size: 13px;
    white-space: nowrap;
`;

const FilterInput = styled.input`
    height: 40px;
    flex: 1;
    min-width: 240px;
    border-radius: 12px;
    border: 1px solid rgba(255, 255, 255, 0.18);
    background: rgba(255, 255, 255, 0.08);
    color: #fff;
    padding: 0 12px;
    box-sizing: border-box;
    outline: none;

    &::placeholder {
        color: rgba(255, 255, 255, 0.7);
    }

    &:focus {
        border-color: rgba(0, 123, 255, 0.9);
        box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
    }
`;

const TableShell = styled.div`
    width: 100%;
    flex: 1;
    min-height: 0;
    background-color: #ffffff;
    border-radius: 15px;
    overflow: hidden;
    display: flex;
    flex-direction: column;
`;

const TableFlexFill = styled.div`
    flex: 1;
    min-height: 0;
    width: 100%;
    display: flex;
    flex-direction: column;

    & > div {
        flex: 1;
        min-height: 0;
        display: flex;
        flex-direction: column;
    }
`;

const GrupoSelectCell = styled.div`
    display: flex;
    align-items: center;
    padding: 6px 0;
    box-sizing: border-box;
`;

const GrupoSelect = styled.select`
    appearance: none;
    -webkit-appearance: none;
    -moz-appearance: none;
    width: 100%;
    max-width: 260px;
    height: 34px;
    border-radius: 8px;
    border: 1px solid rgba(0, 0, 0, 0.18);
    background-color: #fff;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23242d34' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 14px center;
    background-size: 12px;
    color: #242d34;
    padding: 0 36px 0 10px;
    box-sizing: border-box;
    font-size: 13px;
    outline: none;
    cursor: pointer;

    &:focus {
        border-color: rgba(0, 123, 255, 0.9);
        box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.2);
    }

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
`;

function buildSearchHaystack(row) {
    if (!row) return "";

    return [row.usuario, row.user_id, row.email, row.grupo_nome]
        .filter((v) => v != null && String(v).trim() !== "" && String(v).trim() !== "—")
        .join(" ")
        .toUpperCase();
}

const GestaoAcessosV2 = () => {
    const api = useApi();
    const { isModalOpen } = useModal();
    const { notifySuccess, notifyError } = useToast();
    const tableRef = useRef(null);

    const [loading, setLoading] = useState(true);
    const [usuarios, setUsuarios] = useState([]);
    const [grupos, setGrupos] = useState([]);
    const [modulosGerenciaveis, setModulosGerenciaveis] = useState([]);
    const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
    const [savingId, setSavingId] = useState(null);
    const [q, setQ] = useState("");

    const managedModulos = useMemo(
        () => new Set(modulosGerenciaveis.filter(Boolean)),
        [modulosGerenciaveis]
    );

    const gruposAtribuiveis = useMemo(
        () =>
            managedModulos.size === 0
                ? []
                : grupos.filter((g) => managedModulos.has(g.modulo)),
        [grupos, managedModulos]
    );

    const canManageCurrentGrupo = useCallback(
        (row) => {
            if (row?.grupo_id == null) return true;
            if (!row?.grupo_modulo) return false;
            return managedModulos.has(row.grupo_modulo);
        },
        [managedModulos]
    );

    const getGrupoOptionsForRow = useCallback(
        (row) => {
            const options = [...gruposAtribuiveis];
            const currentId = row?.grupo_id != null ? Number(row.grupo_id) : null;

            if (
                currentId != null &&
                !options.some((g) => Number(g.id) === currentId) &&
                row?.grupo_nome
            ) {
                options.unshift({
                    id: currentId,
                    nome: row.grupo_nome,
                    modulo: row.grupo_modulo,
                    $outsideScope: true,
                });
            }

            return options;
        },
        [gruposAtribuiveis]
    );

    const fetchData = useCallback(async () => {
        setLoading(true);

        try {
            const [usuariosRes, gruposRes, modulosRes] = await Promise.all([
                api.get(LIST_USUARIOS_URL),
                api.get(LIST_GRUPOS_URL),
                api.get(LIST_MODULOS_URL),
            ]);

            setUsuarios(Array.isArray(usuariosRes.data) ? usuariosRes.data : []);
            setGrupos(Array.isArray(gruposRes.data) ? gruposRes.data : []);
            setModulosGerenciaveis(Array.isArray(modulosRes.data) ? modulosRes.data : []);
        } catch (error) {
            console.error(error);
            setUsuarios([]);
            setGrupos([]);
            setModulosGerenciaveis([]);
            notifyError(error?.response?.data?.message || "Erro ao carregar usuários.");
        } finally {
            setLoading(false);
        }
    }, [api, notifyError]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const filtered = useMemo(() => {
        const query = q.trim().toUpperCase();
        if (!query) return usuarios;

        return usuarios.filter((row) => buildSearchHaystack(row).includes(query));
    }, [usuarios, q]);

    useEffect(() => {
        setVisibleCount(PAGE_SIZE);
        if (tableRef.current) {
            tableRef.current.scrollTop = 0;
        }
    }, [filtered]);

    const loadMoreRows = useCallback(() => {
        setVisibleCount((prev) => {
            const total = filtered.length;
            if (prev >= total) return prev;
            return Math.min(prev + PAGE_SIZE, total);
        });
    }, [filtered.length]);

    const handleChangeGrupo = useCallback(
        async (row, nextGrupoIdRaw) => {
            if (!row?.id || savingId != null) return;

            const nextGrupoId =
                nextGrupoIdRaw === "" || nextGrupoIdRaw == null ? null : Number(nextGrupoIdRaw);
            const currentGrupoId = row.grupo_id != null ? Number(row.grupo_id) : null;

            if (nextGrupoId === currentGrupoId) return;

            const previous = {
                grupo_id: row.grupo_id,
                grupo_nome: row.grupo_nome,
                grupo_modulo: row.grupo_modulo,
            };

            const nextGrupo =
                nextGrupoId == null
                    ? null
                    : grupos.find((g) => Number(g.id) === nextGrupoId) || null;

            setSavingId(row.id);
            setUsuarios((prev) =>
                prev.map((u) =>
                    Number(u.id) === Number(row.id)
                        ? {
                              ...u,
                              grupo_id: nextGrupoId,
                              grupo_nome: nextGrupo?.nome ?? null,
                              grupo_modulo: nextGrupo?.modulo ?? null,
                          }
                        : u
                )
            );

            try {
                const { data } = await api.post(SET_GRUPO_URL, {
                    id: row.id,
                    grupo_id: nextGrupoId,
                });

                setUsuarios((prev) =>
                    prev.map((u) =>
                        Number(u.id) === Number(row.id)
                            ? {
                                  ...u,
                                  grupo_id: data?.grupo_id ?? nextGrupoId,
                                  grupo_nome: data?.grupo_nome ?? nextGrupo?.nome ?? null,
                                  grupo_modulo: data?.grupo_modulo ?? nextGrupo?.modulo ?? null,
                              }
                            : u
                    )
                );
                notifySuccess(data?.message || "Grupo atualizado com sucesso.");
            } catch (error) {
                console.error(error);
                setUsuarios((prev) =>
                    prev.map((u) =>
                        Number(u.id) === Number(row.id)
                            ? { ...u, ...previous }
                            : u
                    )
                );
                notifyError(error?.response?.data?.message || "Erro ao atualizar grupo do usuário.");
            } finally {
                setSavingId(null);
            }
        },
        [api, grupos, notifyError, notifySuccess, savingId]
    );

    const columns = useMemo(
        () => [
            {
                id: "usuario",
                header: "NOME",
                accessor: "usuario",
                sortable: true,
                render: (value) => (value != null && String(value).trim() !== "" ? String(value).trim() : "—"),
            },
            {
                id: "user_id",
                header: "USUÁRIO",
                accessor: "user_id",
                sortable: true,
                width: "180px",
                render: (value) => (value != null && String(value).trim() !== "" ? String(value).trim() : "—"),
            },
            {
                id: "email",
                header: "E-MAIL",
                accessor: "email",
                sortable: true,
                render: (value) => (value != null && String(value).trim() !== "" ? String(value).trim() : "—"),
            },
            {
                id: "grupo_nome",
                header: "GRUPO",
                accessor: (row) => row.grupo_nome || "",
                sortable: true,
                width: "240px",
                render: (_value, row) => {
                    const options = getGrupoOptionsForRow(row);
                    const canClear = canManageCurrentGrupo(row);
                    const noManagedGroups = gruposAtribuiveis.length === 0;

                    return (
                        <GrupoSelectCell>
                            <GrupoSelect
                                value={row.grupo_id != null ? String(row.grupo_id) : ""}
                                disabled={savingId === row.id || noManagedGroups}
                                onClick={(e) => e.stopPropagation()}
                                onChange={(e) => {
                                    e.stopPropagation();
                                    handleChangeGrupo(row, e.target.value);
                                }}
                                aria-label={`Grupo do usuário ${row.usuario || row.user_id || row.id}`}
                            >
                                {canClear && <option value="">Sem grupo</option>}
                                {options.map((grupo) => (
                                    <option key={grupo.id} value={String(grupo.id)}>
                                        {grupo.nome}
                                    </option>
                                ))}
                            </GrupoSelect>
                        </GrupoSelectCell>
                    );
                },
            },
        ],
        [canManageCurrentGrupo, getGrupoOptionsForRow, gruposAtribuiveis.length, handleChangeGrupo, savingId]
    );

    return (
        <PageContent>
            <HeaderBar>
                <Badge>Total: {loading ? "..." : filtered.length}</Badge>
                <FilterInput
                    placeholder="Pesquisar por nome, usuário, e-mail ou grupo..."
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                />
            </HeaderBar>

            <TableShell>
                <TableFlexFill>
                    <SortableTable
                        ref={tableRef}
                        columns={columns}
                        data={filtered}
                        getRowId={(row) => row.id}
                        isLoading={loading}
                        loadingText="Carregando usuários..."
                        emptyText="NENHUM USUÁRIO ENCONTRADO"
                        initialSort={{ key: "usuario", direction: "asc" }}
                        limit={visibleCount}
                        onScrollReachEnd={loadMoreRows}
                        scrollReachThreshold={SCROLL_THRESHOLD}
                    />
                </TableFlexFill>
            </TableShell>

            {isModalOpen(MODAL_GERENCIAR_GRUPOS_ID) && (
                <ModalGerenciarGrupos onSaved={fetchData} />
            )}

            {isModalOpen(MODAL_NOVO_GRUPO_ID) && (
                <ModalNovoGrupo onSaved={fetchData} />
            )}
        </PageContent>
    );
};

export default GestaoAcessosV2;
